import { SortDropdown } from '@components/sort-dropdown';
import { BtnPrimary, BtnSecondary, Button } from '@components/buttons';
import { BaseDialog, BtnCancel } from '@components/dialog';
import {
  BeforeUploadDecision,
  filepicker,
  FilePickerOpts,
  FilePickerResult,
  multiFilePicker,
} from '@components/filepicker';
import { rpx } from 'client/lib/rpx-client';
import { showReusePrompt } from './reuse-prompt';
import { showError } from '@components/app-error';
import { IcoGrid, IcoList, IcoPaperclip, IcoX } from '@components/icons';
import { LoadingIndicator } from '@components/loading-indicator';
import { showModalForm } from '@components/modal-form';
import { SearchBox } from '@components/search-box';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useIntl } from 'shared/intl/use-intl';
import { useWindowDimensions } from 'client/lib/hooks/use-window-dimensions';
import { CategoryFilter } from './category-filter';
import { CourseFilter, useCourseTitle } from './course-filter';
import { FolderList } from './folder-list';
import { TagFilter } from './tag-filter';
import { TagDrawer } from './tag-drawer';
import { FileThumbnail } from './file-thumbnail';
import { MediaList } from './media-list';
import { showMediaPreview } from './preview-modal';
import { filtersEqual, useMediaManager } from './use-media-manager';
import { FolderFilter, ManagedFile, ManagedFolder, MediaManagerMode, SortBy, View } from './state';

export { showMediaPreview } from './preview-modal';
export type { ManagedFile } from './state';
export type { MediaManagerMode } from './state';

type Props = {
  mode: MediaManagerMode;
  /** Used in pick mode to narrow files by accepted MIME types (e.g. "image/*"). */
  accept?: string;
  /** Required in pick mode — called when the user picks a file (always one from the grid). */
  onPick?(files: ManagedFile[]): void;
  /** Increment to force a refresh of the current page. Used after external uploads. */
  refreshSignal?: number;
  /** Elements rendered at the right edge of the tabs row (manage mode) or above the filters (pick mode). */
  headerActions?: JSX.Element;
  /** When true, filter state is read from and written to the URL query string. */
  persistUrlState?: boolean;
  /**
   * When true, surface the folder bar, "Move to folder" row action, and pass
   * the active folderId to uploads. Default false so student-facing callers
   * (comment editor, etc.) get the existing flat experience.
   */
  enableFolders?: boolean;
  /**
   * Fired (on mount and on every folder change) with the folder currently being
   * viewed. Lets a non-URL caller (the pick modal) route uploads into it.
   */
  onFolderChange?(folderId: FolderFilter): void;
  uploadReceipts?: UploadReceipt[];
  picked?: Map<string, ManagedFile>;
  onTogglePick?(file: ManagedFile): void;
};

export function MediaManager(props: Props) {
  const { headerActions, enableFolders, picked, onTogglePick } = props;
  const pickedIds = useMemo(() => new Set(picked?.keys()), [picked]);
  const {
    state,
    view,
    setView,
    folders,
    tags,
    refreshTags,
    tagSummaries,
    tagSummaryStatus,
    selected,
    isManage,
    isArchived,
    folderSelected,
    hasOtherFilters,
    loadMore,
    reload,
    toggleSelect,
    clearSelection,
    changeCategory,
    changeCourse,
    changeFolder,
    clearFilters,
    openFolder,
    changeTag,
    openTagFiles,
    changeSort,
    changeSearch,
    createFolder,
    renameFolder,
    deleteFolder,
    createTag,
    renameTag,
    deleteTag,
    moveFiles,
    editFileTags,
    editSelectedTags,
    setArchived,
    rename,
    handleClick,
  } = useMediaManager(props);
  // Grid view is desktop-only; phones always render the list so rows can stack.
  const { isMobile } = useWindowDimensions();
  const effectiveView = isMobile ? 'list' : view;
  const course = useCourseTitle(isManage ? state.courseId : undefined);
  const courseLabel = course.title ?? 'Selected course';
  const [organizationOpen, setOrganizationOpen] = useState(false);
  const [tagDrawerOpen, setTagDrawerOpen] = useState(false);
  function closeTagDrawer() {
    setTagDrawerOpen(false);
    requestAnimationFrame(() => document.getElementById('media-tag-filter-trigger')?.focus());
  }
  const [revealed, setRevealed] = useState<{ file: ManagedFile; pinned: boolean }>();
  const pendingScroll = useRef(false);
  const lookupRequest = useRef(0);
  const latestState = useRef(state);
  latestState.current = state;
  const revealedIsCurrent =
    !!revealed &&
    state.folderId === (revealed.file.folderId ?? 'root') &&
    state.category === 'all' &&
    !state.courseId &&
    !state.tagId &&
    !state.search &&
    state.sortBy === 'created';
  const revealedInGrid = !!revealed && state.items.some((file) => file.id === revealed.file.id);
  const showPinned =
    !!revealed?.pinned && !revealedInGrid && (!state.isLoading || state.items.length > 0);
  useEffect(() => {
    if (!revealed) {
      return;
    }
    if (!revealedIsCurrent) {
      setRevealed(undefined);
      pendingScroll.current = false;
      return;
    }
    if (revealedInGrid && revealed.pinned) {
      setRevealed({ ...revealed, pinned: false });
    }
    if (pendingScroll.current && (revealedInGrid || showPinned)) {
      pendingScroll.current = false;
      const id = revealedInGrid ? `media-file-${revealed.file.id}` : 'media-pinned-target';
      requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: 'center' }));
    }
  }, [revealed, revealedIsCurrent, revealedInGrid, showPinned]);

  async function revealReceiptFile(fileId: string, trigger: HTMLButtonElement) {
    const request = ++lookupRequest.current;
    const before = latestState.current;
    function isStale() {
      return request !== lookupRequest.current || !filtersEqual(latestState.current, before);
    }
    try {
      const { file } = await rpx.mediaManager.getManagedFile({ fileId });
      if (isStale()) {
        return;
      }
      openFolder(file.folderId ?? 'root');
      if (!file.archivedOn) {
        pendingScroll.current = true;
        setRevealed({ file, pinned: true });
      }
    } catch (err) {
      if (!isStale()) {
        showError(err);
        trigger.focus();
      }
    }
  }
  const scopeName =
    state.folderId === 'archived'
      ? 'Archived'
      : state.folderId === 'root'
        ? 'Unfiled'
        : (folders.find((folder) => folder.id === state.folderId)?.name ??
          (state.folderId ? 'Folder unavailable' : 'All files'));
  const tagName =
    tags.find((tag) => tag.id === state.tagId)?.name ??
    (state.tagId ? 'Unavailable tag' : undefined);
  const activeFilters = [
    state.category !== 'all' && {
      label: `Type: ${{ image: 'Images', video: 'Videos', audio: 'Audio', document: 'Documents' }[state.category]}`,
      aria: 'Remove type filter',
      onClick: () => changeCategory('all'),
    },
    state.courseId && {
      label: `Course: ${courseLabel}`,
      aria: 'Remove course filter',
      onClick: () => changeCourse(undefined),
    },
    state.tagId && {
      label: `Tag: ${tagName}`,
      aria: 'Remove tag filter',
      onClick: () => changeTag(undefined),
    },
    state.search && {
      label: `Search: ${state.search}`,
      aria: 'Remove search filter',
      onClick: () => changeSearch(''),
    },
  ].filter(Boolean) as { label: string; aria: string; onClick(): void }[];

  const showOrganization = enableFolders && (isManage || folders.length > 0);
  const organization = showOrganization ? (
    <FolderList
      folders={folders}
      current={state.folderId}
      showArchived={isManage}
      canManage={isManage}
      onSelect={(folderId) => {
        changeFolder(folderId);
        setOrganizationOpen(false);
      }}
      onNewFolder={isManage ? createFolder : undefined}
      onRename={isManage ? renameFolder : undefined}
      onDelete={isManage ? deleteFolder : undefined}
    />
  ) : undefined;

  return (
    <div class={showOrganization ? 'md:grid md:grid-cols-[14rem_minmax(0,1fr)] md:gap-6' : ''}>
      {showOrganization && (
        <aside class="hidden md:block border-r border-gray-200 pr-4">{organization}</aside>
      )}
      <main class="flex min-w-0 flex-col gap-4">
        {showOrganization && (
          <div class="md:hidden">
            <BtnSecondary
              onClick={() => setOrganizationOpen((open) => !open)}
              aria-expanded={organizationOpen}
              aria-controls="media-organization-mobile"
              class="w-full justify-center"
            >
              Folders
            </BtnSecondary>
            {organizationOpen && (
              <div
                id="media-organization-mobile"
                class="mt-2 rounded-lg border border-gray-200 bg-white p-3"
              >
                {organization}
              </div>
            )}
          </div>
        )}

        {enableFolders && (
          <div class="flex flex-wrap items-baseline gap-2" aria-live="polite">
            <h2 class="text-lg font-semibold text-gray-900">{scopeName}</h2>
            <span class="text-sm text-gray-500" role="status">
              {state.hasError
                ? 'Files could not load'
                : state.isLoading
                  ? state.search.trim()
                    ? 'Searching…'
                    : 'Loading files…'
                  : `${state.items.length}${state.hasMore ? '+' : ''} result${state.items.length === 1 && !state.hasMore ? '' : 's'} shown`}
            </span>
          </div>
        )}

        <div class="flex flex-col gap-3">
          <div class="flex flex-col md:flex-row md:flex-wrap md:items-center gap-3 md:justify-between">
            <SearchBox
              containerClass="w-full md:max-w-md md:min-w-64 md:flex-1"
              class="ruz-input text-sm pl-9"
              placeholder="Search by file name or ID"
              value={state.search}
              onTermChange={changeSearch}
            />
            <div class="flex flex-wrap items-center gap-3">
              <SortDropdown
                options={SORT_OPTIONS}
                value={state.sortBy}
                onChange={changeSort}
                class="min-h-10.5"
              />
              {!isMobile && <ViewToggle value={view} onChange={setView} />}
              {headerActions}
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-2" role="group" aria-label="Filter files">
            {!props.accept && <CategoryFilter value={state.category} onChange={changeCategory} />}
            {isManage && (
              <CourseFilter
                value={state.courseId}
                label={courseLabel}
                filters={{
                  category: state.category,
                  search: state.search,
                  folderId: state.folderId,
                  tagId: state.tagId,
                }}
                onRemember={course.remember}
                onChange={changeCourse}
              />
            )}
            {enableFolders && (isManage || tags.length > 0) && (
              <TagFilter
                tags={tags}
                value={state.tagId}
                onChange={changeTag}
                onManage={
                  isManage
                    ? () => {
                        setTagDrawerOpen(true);
                        void refreshTags();
                      }
                    : undefined
                }
              />
            )}
          </div>
          {enableFolders && activeFilters.length > 0 && (
            <div class="flex flex-wrap items-center gap-2" aria-label="Active filters">
              {activeFilters.map((filter) => (
                <button
                  key={filter.aria}
                  type="button"
                  onClick={filter.onClick}
                  aria-label={filter.aria}
                  class="inline-flex min-h-9 max-w-full items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-sm text-indigo-800 hover:bg-indigo-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                >
                  <span class="min-w-0 truncate" title={filter.label}>
                    {filter.label}
                  </span>
                  <IcoX class="size-3.5 shrink-0" />
                </button>
              ))}
              <button
                type="button"
                onClick={clearFilters}
                class="min-h-9 rounded-md px-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>

        {isManage && props.uploadReceipts && props.uploadReceipts.length > 0 && (
          <UploadReceipts
            receipts={props.uploadReceipts}
            folders={folders}
            onOpenFolder={revealReceiptFile}
          />
        )}

        {showPinned && revealed && <PinnedFileCard file={revealed.file} />}

        {isManage && enableFolders && selected.size > 0 && (
          <SelectionBar
            count={selected.size}
            onMove={() => moveFiles([...selected])}
            onEditTags={editSelectedTags}
            onClear={clearSelection}
          />
        )}

        {state.hasError ? (
          <div
            role="alert"
            class="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-900"
          >
            Files could not load.{' '}
            <BtnSecondary onClick={() => reload()} class="ml-2 rounded-full">
              Try again
            </BtnSecondary>
          </div>
        ) : state.isLoading && state.items.length === 0 ? (
          <LoadingIndicator />
        ) : (
          <MediaList
            items={state.items}
            searchTerm={state.search}
            activeTagId={state.tagId}
            showAddedDate={state.sortBy === 'created'}
            archivedView={isArchived}
            filtered={hasOtherFilters}
            folderEmpty={folderSelected && !hasOtherFilters}
            scopeName={
              state.folderId === 'root' ||
              state.folderId === 'archived' ||
              folders.some((folder) => folder.id === state.folderId)
                ? scopeName
                : undefined
            }
            onClearFilters={hasOtherFilters ? clearFilters : undefined}
            view={effectiveView}
            showActions={isManage}
            selectable={isManage && enableFolders}
            selected={onTogglePick ? pickedIds : selected}
            multiSelect={!!onTogglePick}
            highlightedFileId={revealed?.file.id}
            onToggleSelect={toggleSelect}
            onClick={onTogglePick ?? handleClick}
            onArchive={isManage ? (file) => setArchived(file, true) : undefined}
            onUnarchive={isManage ? (file) => setArchived(file, false) : undefined}
            onRename={isManage ? rename : undefined}
            onMove={isManage && enableFolders ? (file) => moveFiles([file.id]) : undefined}
            onEditTags={isManage && enableFolders ? editFileTags : undefined}
          />
        )}

        {state.hasMore && (
          <footer class="text-center pt-2">
            <BtnSecondary isLoading={state.isLoading} onClick={loadMore} class="rounded-full">
              Load more
            </BtnSecondary>
          </footer>
        )}
      </main>
      {isManage && enableFolders && (
        <TagDrawer
          open={tagDrawerOpen}
          tags={tags}
          summaries={tagSummaries}
          summaryStatus={tagSummaryStatus}
          onRetrySummaries={refreshTags}
          onClose={closeTagDrawer}
          onCreate={createTag}
          onRename={renameTag}
          onDelete={deleteTag}
          onViewFiles={(tagId) => {
            openTagFiles(tagId);
            closeTagDrawer();
          }}
        />
      )}
    </div>
  );
}

function SelectionBar({
  count,
  onMove,
  onEditTags,
  onClear,
}: {
  count: number;
  onMove(): void;
  onEditTags(): void;
  onClear(): void;
}) {
  return (
    <div class="sticky top-0 z-10 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3">
      <span class="flex-1 text-sm font-medium text-indigo-900 sm:flex-none">
        {count} file{count === 1 ? '' : 's'} selected
      </span>
      <div class="text-sm">
        <BtnCancel
          onClick={onClear}
          class="min-h-11 rounded-full px-4 py-2 transition-all hover:bg-gray-100 sm:min-h-0"
        >
          Clear selection
        </BtnCancel>
      </div>
      <div class="flex w-full flex-wrap items-center gap-3 sm:ml-auto sm:w-auto">
        <BtnPrimary onClick={onMove} class="min-h-11 rounded-full sm:min-h-0">
          Move to folder…
        </BtnPrimary>
        <BtnSecondary onClick={onEditTags} class="min-h-11 rounded-full bg-white sm:min-h-0">
          Edit tags…
        </BtnSecondary>
      </div>
    </div>
  );
}

const SORT_OPTIONS: Array<{ value: SortBy; label: string }> = [
  { value: 'recent', label: 'Recently used' },
  { value: 'created', label: 'Date added' },
];

function ViewToggle({ value, onChange }: { value: View; onChange(v: View): void }) {
  return (
    <div class="inline-flex bg-gray-100 rounded-full p-1 gap-1">
      {(['grid', 'list'] as const).map((v) => {
        const Icon = v === 'grid' ? IcoGrid : IcoList;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            aria-pressed={value === v}
            aria-label={v === 'grid' ? 'Grid view' : 'List view'}
            class={`p-1.5 rounded-full transition-colors ${
              value === v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            <Icon class="size-4" />
          </button>
        );
      })}
    </div>
  );
}

async function previewFile(fileId: string, trigger: HTMLButtonElement) {
  try {
    const { file } = await rpx.mediaManager.getManagedFile({ fileId });
    await showMediaPreview(file);
  } catch (err) {
    showError(err);
  }
  trigger.focus();
}

function PinnedFileCard({ file }: { file: ManagedFile }) {
  return (
    <section
      id="media-pinned-target"
      aria-label="File from recent upload"
      class="rounded-lg border-2 border-indigo-400 bg-indigo-50 p-3"
    >
      <h3 class="text-sm font-semibold text-indigo-900">File from recent upload</h3>
      <div class="mt-2 grid grid-cols-[3rem_minmax(0,1fr)] items-center gap-3 sm:grid-cols-[3rem_minmax(0,1fr)_auto]">
        <FileThumbnail
          type={file.type}
          name={file.name}
          publicUrl={file.publicUrl}
          posterUrl={file.posterUrl}
          class="size-12 rounded"
        />
        <span class="min-w-0 flex-1 break-words text-sm font-medium text-gray-900">
          {file.name}
        </span>
        <div class="col-span-2 sm:col-span-1 sm:col-start-3">
          <BtnSecondary
            onClick={(event) => void previewFile(file.id, event.currentTarget as HTMLButtonElement)}
            class="rounded-full bg-white"
          >
            View file
          </BtnSecondary>
        </div>
      </div>
    </section>
  );
}

function UploadReceipts({
  receipts,
  folders,
  onOpenFolder,
}: {
  receipts: UploadReceipt[];
  folders: ManagedFolder[];
  onOpenFolder(fileId: string, trigger: HTMLButtonElement): Promise<void>;
}) {
  const [dismissed, setDismissed] = useState<string[]>([]);
  const visible = receipts.filter((receipt) => !dismissed.includes(receipt.receiptId));
  if (visible.length === 0) {
    return;
  }
  return (
    <section
      aria-label="Recent uploads"
      class="rounded-lg border border-green-200 bg-green-50 p-3"
      aria-live="polite"
    >
      <div class="flex items-center justify-between gap-3">
        <h3 class="text-sm font-semibold text-green-900">
          {visible.length} file{visible.length === 1 ? '' : 's'} added
        </h3>
        <div class="text-sm text-green-900">
          <BtnCancel onClick={() => setDismissed(receipts.map((receipt) => receipt.receiptId))}>
            Dismiss
          </BtnCancel>
        </div>
      </div>
      <ul class="mt-2 flex max-h-64 flex-col gap-2 overflow-y-auto">
        {visible.map((receipt) => {
          const file = receipt.file;
          const destination =
            receipt.metadataState === 'loading'
              ? 'Checking destination…'
              : receipt.metadataState === 'unavailable'
                ? 'Destination unavailable'
                : receipt.folderId
                  ? (folders.find((folder) => folder.id === receipt.folderId)?.name ??
                    'Folder unavailable')
                  : 'Unfiled';
          return (
            <li
              key={receipt.receiptId}
              class="grid grid-cols-[3rem_minmax(0,1fr)] items-center gap-3 rounded-md border border-green-100 bg-white p-2 sm:grid-cols-[3rem_minmax(0,1fr)_auto]"
            >
              <FileThumbnail
                type={file?.type ?? receipt.result.type}
                name={file?.name ?? receipt.result.name}
                publicUrl={file?.publicUrl ?? receipt.result.publicUrl}
                posterUrl={file?.posterUrl}
                class="size-12 rounded"
              />
              <div class="min-w-0 flex-1 text-sm">
                <div class="font-medium text-gray-900 break-words">
                  {file?.name ?? receipt.result.name}
                </div>
                <div class="break-words text-gray-600">{destination}</div>
              </div>
              <div class="col-span-2 flex flex-wrap gap-2 sm:col-span-1 sm:col-start-3">
                <BtnSecondary
                  onClick={(event) =>
                    void previewFile(
                      receipt.result.fileId,
                      event.currentTarget as HTMLButtonElement,
                    )
                  }
                  class="rounded-full"
                >
                  View file
                </BtnSecondary>
                <BtnSecondary
                  onClick={(event) =>
                    void onOpenFolder(
                      receipt.result.fileId,
                      event.currentTarget as HTMLButtonElement,
                    )
                  }
                  class="rounded-full"
                >
                  Open folder
                </BtnSecondary>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function toManagedFile(r: FilePickerResult): ManagedFile {
  return {
    id: r.fileId,
    name: r.name,
    type: r.type,
    path: r.filePath,
    size: r.size,
    usageCourseCount: 0,
    createdAt: new Date().toISOString(),
    lastImportedAt: new Date().toISOString(),
    archivedOn: undefined,
    folderId: undefined,
    tags: [],
    publicUrl: r.publicUrl,
    url: r.url,
    posterUrl: undefined,
    // A just-uploaded file lives in the legacy bucket until it's transcoded.
    isVideoBucket: false,
  };
}

function toFilePickerResult(file: ManagedFile): FilePickerResult {
  return {
    fileId: file.id,
    filePath: file.path,
    publicUrl: file.publicUrl,
    url: file.url,
    type: file.type,
    name: file.name,
    size: file.size ?? 0,
  };
}

/**
 * Runs before an upload starts: looks for an already-uploaded duplicate and, if
 * found, lets the user reuse it instead of creating a duplicate. Videos match on
 * size + type alone (the same video is often re-uploaded under a different name);
 * other types require a name match too. Reusing bumps the existing file's recency
 * so it surfaces first. Any lookup failure falls through to a normal upload —
 * never blocks the user.
 */
export async function checkForDuplicate(file: File): Promise<BeforeUploadDecision> {
  try {
    const { match } = await rpx.mediaManager.findDuplicateFiles({
      name: file.name,
      size: file.size,
      type: file.type,
    });
    if (!match) {
      return { action: 'proceed' };
    }
    const choice = await showReusePrompt(match);
    if (choice.type === 'reuse') {
      await rpx.mediaManager
        .markFilesImported({ fileIds: [choice.file.id] })
        .catch(() => undefined);
      return { action: 'reuse', result: toFilePickerResult(choice.file) };
    }
    if (choice.type === 'cancel') {
      return { action: 'cancel' };
    }
    return { action: 'proceed' };
  } catch {
    return { action: 'proceed' };
  }
}

type UploadHook = {
  /** Pass to MediaManager to refresh after an upload completes. */
  refreshSignal: number;
  /**
   * Wire to a button onClick. Runs filepicker (single) or multiFilePicker
   * (when multiUpload is true); on success picks the resulting file(s) or
   * refreshes the list.
   */
  upload(folderId?: FolderFilter): Promise<void>;
  isUploading: boolean;
  receipts: UploadReceipt[];
};

export type UploadReceipt = {
  receiptId: string;
  result: FilePickerResult;
  file?: ManagedFile;
  folderId?: string;
  metadataState: 'loading' | 'unavailable' | 'ready';
};

export function useMediaManagerUpload(opts: {
  mode: MediaManagerMode;
  accept?: string;
  uploadOpts?: FilePickerOpts;
  /** When true, the Upload button uses multiFilePicker instead of filepicker. */
  multiUpload?: boolean;
  /** Media-manager folder uploads should land in. Undefined / 'root' → root. */
  folderId?: FolderFilter;
  onPick?(files: ManagedFile[]): void;
}): UploadHook {
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [receipts, setReceipts] = useState<UploadReceipt[]>([]);
  const receiptSequence = useRef(0);

  const upload = useCallback(
    async (chosenFolderId?: FolderFilter) => {
      setIsUploading(true);
      try {
        const active = opts.mode === 'pick' ? opts.folderId : (chosenFolderId ?? opts.folderId);
        const targetFolderId =
          active && active !== 'root' && active !== 'archived' ? active : undefined;

        let picked: FilePickerResult[] | undefined;
        if (opts.multiUpload) {
          picked = await multiFilePicker({
            accept: opts.accept,
            folderId: targetFolderId,
            ...opts.uploadOpts,
            beforeUpload: checkForDuplicate,
          });
        } else {
          const single = await filepicker({
            accept: opts.accept,
            folderId: targetFolderId,
            ...opts.uploadOpts,
            beforeUpload: checkForDuplicate,
          });
          picked = single ? [single] : undefined;
        }
        if (!picked || picked.length === 0) {
          return;
        }
        if (opts.mode === 'pick' && opts.onPick) {
          opts.onPick(picked.map(toManagedFile));
          return;
        }
        const added = picked.map(
          (result): UploadReceipt => ({
            receiptId: String(++receiptSequence.current),
            result,
            metadataState: 'loading',
          }),
        );
        setReceipts((current) => [...current, ...added]);
        setRefreshSignal((s) => s + 1);
        void (async () => {
          for (const addedReceipt of added) {
            try {
              const { file } = await rpx.mediaManager.getManagedFile({
                fileId: addedReceipt.result.fileId,
              });
              setReceipts((current) =>
                current.map((receipt) =>
                  receipt.receiptId === addedReceipt.receiptId
                    ? { ...receipt, file, folderId: file.folderId, metadataState: 'ready' }
                    : receipt,
                ),
              );
            } catch {
              setReceipts((current) =>
                current.map((receipt) =>
                  receipt.receiptId === addedReceipt.receiptId
                    ? { ...receipt, metadataState: 'unavailable' }
                    : receipt,
                ),
              );
            }
          }
        })();
      } finally {
        setIsUploading(false);
      }
    },
    [opts.mode, opts.accept, opts.uploadOpts, opts.multiUpload, opts.folderId, opts.onPick],
  );

  return { refreshSignal, upload, isUploading, receipts };
}

export function UploadButton({ onClick, isLoading }: { onClick(): void; isLoading?: boolean }) {
  return (
    <BtnPrimary
      onClick={onClick}
      isLoading={isLoading}
      class="pl-4 pr-5 rounded-full gap-2 relative disabled:cursor-not-allowed"
    >
      <IcoPaperclip class="size-4" />
      Upload
    </BtnPrimary>
  );
}

export type PickSelection = ReturnType<typeof usePickSelection>;

export function usePickSelection(onPick: (files: ManagedFile[]) => void, multiSelect?: boolean) {
  const [picked, setPicked] = useState<Map<string, ManagedFile>>(() => new Map());
  const pickedRef = useRef(picked);
  pickedRef.current = picked;
  const onUploaded = useCallback(
    (files: ManagedFile[]) => {
      const uploadedIds = new Set(files.map((file) => file.id));
      const earlier = [...pickedRef.current.values()].filter((file) => !uploadedIds.has(file.id));
      if (multiSelect && earlier.length) {
        rpx.mediaManager
          .markFilesImported({ fileIds: earlier.map((file) => file.id) })
          .catch(() => undefined);
      }
      onPick(multiSelect ? [...earlier, ...files] : files);
    },
    [onPick, multiSelect],
  );

  function togglePick(file: ManagedFile) {
    setPicked((current) => {
      const next = new Map(current);
      if (next.has(file.id)) {
        next.delete(file.id);
      } else {
        next.set(file.id, file);
      }
      return next;
    });
  }

  function addPicked() {
    const files = [...picked.values()];
    if (files.length === 0) {
      return;
    }
    rpx.mediaManager
      .markFilesImported({ fileIds: files.map((file) => file.id) })
      .catch(() => undefined);
    onPick(files);
  }

  return {
    picked: multiSelect ? picked : undefined,
    togglePick: multiSelect ? togglePick : undefined,
    count: picked.size,
    addPicked,
    onUploaded,
  };
}

export function PickFooter({
  selection,
  onCancel,
}: {
  selection: PickSelection;
  onCancel(): void;
}) {
  const intl = useIntl();
  return (
    <footer class="px-6 py-3 border-t border-gray-200 flex items-center justify-end gap-3 shrink-0">
      <BtnCancel onClick={onCancel}>{intl('Cancel')}</BtnCancel>
      <BtnPrimary
        onClick={selection.addPicked}
        disabled={selection.count === 0}
        class="rounded-full"
      >
        {selection.count
          ? intl('Add {count:number} {count | pluralize file files}', { count: selection.count })
          : intl('Add')}
      </BtnPrimary>
    </footer>
  );
}

type ModalProps = {
  accept?: string;
  uploadOpts?: FilePickerOpts;
  multiUpload?: boolean;
  multiSelect?: boolean;
  enableFolders?: boolean;
  title?: string;
  onPick(files: ManagedFile[]): void;
  onClose(): void;
};

function MediaManagerModal({
  accept,
  uploadOpts,
  multiUpload,
  multiSelect,
  enableFolders = true,
  title,
  onPick,
  onClose,
}: ModalProps) {
  // Track the folder the user has drilled into so uploads land there. Seeded with
  // 'root' so a stray ?folder= on the page URL can't leak into modal uploads.
  const [folderId, setFolderId] = useState<FolderFilter>('root');
  const selection = usePickSelection(onPick, multiSelect);
  const upload = useMediaManagerUpload({
    mode: 'pick',
    accept,
    uploadOpts,
    multiUpload,
    folderId,
    onPick: selection.onUploaded,
  });

  return (
    <BaseDialog onClose={onClose} contentWidth blurBg={false} zIndex="z-[60]">
      <section class="w-5xl max-w-full h-[80vh] bg-white rounded-lg overflow-hidden flex flex-col">
        <header class="px-6 py-3 border-b border-gray-200 flex items-center justify-between gap-4 shrink-0">
          <h2 class="text-sm font-semibold text-gray-900">{title ?? 'Media library'}</h2>
          <div class="flex items-center gap-2">
            <UploadButton onClick={() => upload.upload()} isLoading={upload.isUploading} />
            <Button
              class="p-2 text-gray-500 hover:text-gray-800"
              onClick={onClose}
              aria-label="Close"
            >
              <IcoX class="size-4" />
            </Button>
          </div>
        </header>
        <div class="flex-1 min-h-0 overflow-auto px-3 py-4 sm:px-6">
          <MediaManager
            mode="pick"
            accept={accept}
            onPick={onPick}
            refreshSignal={upload.refreshSignal}
            enableFolders={enableFolders}
            onFolderChange={setFolderId}
            picked={selection.picked}
            onTogglePick={selection.togglePick}
          />
        </div>
        {multiSelect && <PickFooter selection={selection} onCancel={onClose} />}
      </section>
    </BaseDialog>
  );
}

export type ShowMediaManagerOpts = {
  accept?: string;
  uploadOpts?: FilePickerOpts;
  /** When true, the Upload button accepts multiple files and the promise resolves with an array. */
  multiUpload?: boolean;
  multiSelect?: boolean;
  enableFolders?: boolean;
  title?: string;
};

export function showMediaManager(
  opts: ShowMediaManagerOpts & ({ multiUpload: true } | { multiSelect: true }),
): Promise<ManagedFile[] | undefined>;
export function showMediaManager(
  opts?: ShowMediaManagerOpts & { multiUpload?: false; multiSelect?: false },
): Promise<ManagedFile | undefined>;
export function showMediaManager(
  opts: ShowMediaManagerOpts = {},
): Promise<ManagedFile | ManagedFile[] | undefined> {
  return showModalForm<ManagedFile | ManagedFile[] | undefined>(({ resolve }) => (
    <MediaManagerModal
      accept={opts.accept}
      uploadOpts={opts.uploadOpts}
      multiUpload={opts.multiUpload}
      multiSelect={opts.multiSelect}
      enableFolders={opts.enableFolders}
      title={opts.title}
      onPick={(files) => resolve(opts.multiUpload || opts.multiSelect ? files : files[0])}
      onClose={() => resolve(undefined)}
    />
  ));
}
